import React from "react";
import { useAdminGetCompaniesQuery } from "@diditusa/shared/api/adminApi";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Button, Typography } from "@mui/material";
import BasicTable from "@diditusa/shared/web_components/BasicTable/BasicTable";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import { BasicTableHeader } from "@diditusa/shared/types/commonTypes";
export default function Companies() {
  const {
    data: companies,
    isLoading,
    isError,
    error,
  } = useAdminGetCompaniesQuery();
  const navigate = useNavigate();

  const renderCompanies = () => {
    interface CompanyDetails {
      id: number;
      name: string;
      email: string;
      phoneNumber?: string;
      status?: string;
      imageUrl?: string;
    }

    const companyDetails: CompanyDetails[] =
      companies?.map((c) => ({
        id: c.id,
        name: c.name,
        email: c.contactEmail,
        phoneNumber: c.phoneNumber,
        status: c.status,
        imageUrl: c.imageUrl,
      })) || [];

    const getTable = () => {
      if (isLoading) {
        return <LoadingIndicator isVisible={isLoading} />;
      }

      if (isError) {
        return (
          <Typography variant="caption">{`Error loading companies ${
            (error as any).data.message
          }`}</Typography>
        );
      }
      type CompanyDetailsKey = keyof CompanyDetails;

      const headers: BasicTableHeader<CompanyDetailsKey>[] = [
        { key: "imageUrl", value: "" },
        { key: "name", value: "Name" },
        { key: "email", value: "Email" },
        { key: "phoneNumber", value: "Phone Number" },
        { key: "status", value: "Status" },
      ];

      const getCustomComponentForCell = (
        field: CompanyDetailsKey,
        idx: number
      ) => {
        const c = companyDetails[idx];
        if (field === "imageUrl") {
          return c.imageUrl ? (
            <Avatar src={c.imageUrl} alt={c.name} sx={{ mr: 2 }} />
          ) : (
            <CorporateFareIcon />
          );
        }

        if (field === "phoneNumber") {
          return (
            <Button variant="outlined" sx={{ borderRadius: 5 }}>
              {c.phoneNumber}
            </Button>
          );
        }

        if (field === "status") {
          return (
            <Typography
              fontWeight="bold"
              color={c.status === "Active" ? "green" : "gray"}
            >
              {c.status}
            </Typography>
          );
        }

        return null;
      };

      const onCompanyRowClick = (idx: number) => {
        const cd = companyDetails[idx];
        navigate(`/companies/${cd.id}`);
      };

      return (
        <BasicTable
          headers={headers}
          data={companyDetails}
          getCustomComponentForField={getCustomComponentForCell}
          onRowClick={onCompanyRowClick}
        />
      );
    };

    return (
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Companies
          </Typography>
          <Button
            variant="contained"
            color="inherit"
            sx={{ borderRadius: 10 }}
            onClick={onCreateCompanyClick}
          >
            Add New Company
          </Button>
        </Box>
        {getTable()}
      </Box>
    );
  };

  const onCreateCompanyClick = () => {
    navigate("/companies/create");
  };

  return (
    <Box component="main" sx={{ flexGrow: 1 }}>
      {renderCompanies()}
    </Box>
  );
}
